import React from 'react';
import { cn } from '../../lib/utils';

/**
 * NEXORA Ocean Design System (NODS)
 * Card Component
 * 
 * Based on design_guidelines.json:
 * - Flat background, 1px subtle border, no heavy drop shadows
 * - On hover: slight lift (-translate-y-1) and subtle shadow
 * - 8px border-radius maximum (rounded-lg)
 * - Glassmorphism option for special cases
 */

export const Card = React.forwardRef((
  { className, glass = false, hover = false, children, ...props },
  ref
) => {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-lg p-6',
        glass
          ? 'bg-background/60 backdrop-blur-xl border border-white/10 shadow-glass-dark dark:shadow-glass-dark'
          : 'bg-surface border border-border',
        hover && 'transition-transform duration-200 hover:-translate-y-1 hover:shadow-card-hover',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});

Card.displayName = 'Card';

export const CardHeader = React.forwardRef((
  { className, children, ...props },
  ref
) => {
  return (
    <div
      ref={ref}
      className={cn('flex flex-col space-y-1.5 pb-4', className)}
      {...props}
    >
      {children}
    </div>
  );
});

CardHeader.displayName = 'CardHeader';

export const CardTitle = React.forwardRef((
  { className, children, ...props },
  ref
) => {
  return (
    <h3
      ref={ref}
      className={cn('text-xl font-heading font-medium tracking-tight leading-snug text-text-primary', className)}
      {...props}
    >
      {children}
    </h3>
  );
});

CardTitle.displayName = 'CardTitle';

export const CardDescription = React.forwardRef((
  { className, children, ...props },
  ref
) => {
  return (
    <p
      ref={ref}
      className={cn('text-sm text-text-secondary leading-relaxed', className)}
      {...props}
    >
      {children}
    </p>
  );
});

CardDescription.displayName = 'CardDescription';

export const CardContent = React.forwardRef((
  { className, children, ...props },
  ref
) => {
  return (
    <div
      ref={ref}
      className={cn('pt-0', className)}
      {...props}
    >
      {children}
    </div>
  );
});

CardContent.displayName = 'CardContent';

export const CardFooter = React.forwardRef((
  { className, children, ...props },
  ref
) => {
  return (
    <div
      ref={ref}
      className={cn('flex items-center pt-4', className)}
      {...props}
    >
      {children}
    </div>
  );
});

CardFooter.displayName = 'CardFooter';

export default Card;
